/**
 * Portfolio Export Utilities — CSV only (TASK-407).
 *
 * The legacy browser-print PDF flow (`exportClientPDF`, `exportFullPDF`,
 * `generateClientReportHTML`, `exportPerformanceCSV`) was deleted as part of
 * TASK-407 — portfolio PPTX now goes through the document-export service.
 * Only CSV stays here: it's a local Blob download with no LLM dependency.
 */

import { Position as ApiPosition, AssetAllocation, CashPosition as ApiCashPosition } from '../services/api/portfolioApiService';

// ============================================================================
// CSV Export
// ============================================================================

export const exportToCSV = (
  positions: ApiPosition[],
  allocation: AssetAllocation | null,
  cashPositions: ApiCashPosition[],
  portfolioValue: number
) => {
  const csvRows: string[] = [];

  // Header
  csvRows.push('Type,Security Name,ISIN,Quantity,Price,Currency,Market Value (CHF),Weight (%),Asset Class,Sector');

  // Securities
  positions.forEach((position: ApiPosition) => {
    const weight = portfolioValue > 0 ? (position.market_value / portfolioValue * 100) : 0;
    csvRows.push([
      'Security',
      `"${position.security_name}"`,
      position.isin,
      position.quantity?.toString() || '0',
      position.current_price?.toString() || '0',
      position.currency,
      position.market_value?.toString() || '0',
      weight.toFixed(2),
      position.asset_class || '',
      position.sector || ''
    ].join(','));
  });

  // Empty line
  csvRows.push('');

  // Cash positions
  csvRows.push('Type,Currency,Amount,Amount (CHF),Exchange Rate,Weight (%)');
  cashPositions.forEach((cash: ApiCashPosition) => {
    // TASK-865: was `cash.amount_chf ?? cash.amount` — the fallback always fired, so a
    // foreign balance was exported under a column headed "Amount (CHF)".
    // TASK-865 review: `?? 0` exported "0" under "Amount (CHF)" and "0.0000" under
    // "Exchange Rate" for a balance the platform cannot price — the exact defect this ticket
    // filed against CompositionTab:601, relocated into the artefact that LEAVES the platform.
    const amountChf = cash.amount_base ?? null;
    const weight =
      amountChf !== null && portfolioValue > 0 ? (amountChf / portfolioValue) * 100 : null;
    csvRows.push([
      'Cash',
      cash.currency,
      cash.amount.toString(),
      amountChf === null ? 'no rate' : amountChf.toString(),
      amountChf === null
        ? 'no rate'
        : (cash.amount > 0 ? (amountChf / cash.amount).toFixed(4) : '1.0000'),
      weight === null ? 'no rate' : weight.toFixed(2)
    ].join(','));
  });

  // Empty line
  csvRows.push('');

  // Allocation summary
  if (allocation?.by_asset_class) {
    csvRows.push('Asset Allocation');
    csvRows.push('Asset Class,Percentage (%)');
    Object.entries(allocation.by_asset_class).forEach(([assetClass, percentage]) => {
      csvRows.push(`${assetClass},${percentage.toFixed(2)}`);
    });
  }

  // Create blob and download
  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `portfolio-composition-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
